/**
 * Plugin to provide build information to the Vue application
 * with automatic updates and immediate page refresh when build changes
 * Checks for updates on page navigation and every 30 seconds
 */
import Vue from 'vue';

// Local storage key for storing build info
const STORAGE_KEY = 'app_build_info';

// Minimum time between checks in milliseconds (5 seconds)
// This prevents excessive API calls when navigating quickly between pages
const MIN_CHECK_INTERVAL = 5000;

export default async function ({ $axios, app }, inject) {
  // Create a reactive object for build info
  const buildInfoData = Vue.observable({
    date: '',
    timestamp: 0,
    formattedDate: 'Loading...',
    lastChecked: 0
  });
  
  // Function to load build info from localStorage
  const loadFromStorage = () => {
    if (!process.client) return null;
    
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error loading build info from localStorage:', error);
      return null;
    }
  };
  
  // Function to save build info to localStorage
  const saveToStorage = (data) => {
    if (!process.client) return;
    
    try {
      const toSave = {
        timestamp: data.timestamp,
        date: data.date,
        formattedDate: data.formattedDate
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch (error) {
      console.error('Error saving build info to localStorage:', error);
    }
  };
  
  // Function to check if build has changed
  const hasBuildChanged = (newData, oldData) => {
    return oldData && newData && newData.timestamp !== oldData.timestamp;
  };
  
  // Function to trigger immediate page refresh
  const triggerRefresh = () => {
    // Refresh the page immediately
    if (process.client) {
      window.location.reload();
    }
  };
  
  // Function to fetch build info
  const fetchBuildInfo = async () => {
    try {
      // Add cache-busting parameter to prevent browser caching
      const response = await $axios.get(`/api/build-info?_t=${Date.now()}`);
      const data = response.data;
      const storedData = loadFromStorage();
      
      // Update the reactive data
      if (data.timestamp !== buildInfoData.timestamp) {
        buildInfoData.date = data.date;
        buildInfoData.timestamp = data.timestamp;
        buildInfoData.formattedDate = data.formattedDate;
        // console.log('Build info updated:', buildInfoData.formattedDate);
        
        // Save to localStorage
        saveToStorage(data);
        
        // Check if build has changed since last known build
        if (hasBuildChanged(data, storedData)) {
          console.log('Build has changed! Refreshing page immediately...');
          triggerRefresh();
        }
      }
      
      buildInfoData.lastChecked = Date.now();
    } catch (error) {
      console.error('Error fetching build info:', error);
      // Don't update on error, keep existing data
    }
  };
  
  // Load initial data from localStorage
  const storedData = loadFromStorage();
  if (storedData) {
    buildInfoData.date = storedData.date;
    buildInfoData.timestamp = storedData.timestamp;
    buildInfoData.formattedDate = storedData.formattedDate;
  }
  
  // Initial fetch
  await fetchBuildInfo();
  
  if (process.client) {
    // Set up periodic checking (every 30 seconds)
    // setInterval(fetchBuildInfo, 30000);
    
    // Track last check time to prevent excessive API calls
    let lastCheckTime = Date.now();
    
    // Set up navigation event listeners to check on page change
    if (app && app.router) {
      // For Nuxt 2.x - Check on route change
      // app.router.afterEach(() => {
      //   const now = Date.now();
      //   // Only check if enough time has passed since the last check
      //   if (now - lastCheckTime > MIN_CHECK_INTERVAL) {
      //     fetchBuildInfo();
      //     lastCheckTime = now;
      //   }
      // });
      
      // Also check when user becomes active after being away
      // The 'focus' event is not reliable on all browsers (e.g., Safari on iOS).
      // The 'visibilitychange' event is a more robust alternative.
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          const now = Date.now();
          // Only check if enough time has passed since the last check
          if (now - lastCheckTime > MIN_CHECK_INTERVAL) {
            fetchBuildInfo();
            lastCheckTime = now;
          }
        }
      });

      // The 'pageshow' event is fired when a session history entry is being traversed to.
      // This is especially useful for mobile browsers that may use a back-forward cache.
      window.addEventListener('pageshow', (event) => {
        // The persisted property is false on initial page load.
        // We only want to check on subsequent displays from the cache.
        if (event.persisted) {
          const now = Date.now();
          // Only check if enough time has passed since the last check
          if (now - lastCheckTime > MIN_CHECK_INTERVAL) {
            fetchBuildInfo();
            lastCheckTime = now;
          }
        }
      });
    }
  }
  
  // Inject the reactive build info into the Vue instance
  inject('buildInfo', buildInfoData);
}
